import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { PhoneCall, Phone, MessageCircle, Send, MessageSquare } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const CALLBACK_KEY = "bridgeCallbackNumber";
const telDigits = (p) => (p || "").replace(/[^\d+]/g, "");
const waDigits = (p) => { const d = (p || "").replace(/\D/g, ""); return d.startsWith("0") ? "44" + d.slice(1) : d; };
const inputCls = "w-full bg-white border border-[#D9E0DC] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 outline-none px-3 py-2 text-sm";
const linkBtn = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold bg-white hover:bg-[#F2F8F5]";

const CallPanel = ({ member, onCalled }) => {
  const [cb, setCb] = useState(() => localStorage.getItem(CALLBACK_KEY) || "");
  const [calling, setCalling] = useState(false);
  const start = async () => {
    if (!cb.trim()) { toast.error("Enter the number we should ring you on"); return; }
    localStorage.setItem(CALLBACK_KEY, cb.trim());
    setCalling(true);
    try {
      await api.post("/voice/click-to-call", { to_phone: member.phone, staff_phone: cb.trim(), to_name: member.name, member_id: member.id });
      toast.success("Calling you now — pick up and we'll connect you. Record the outcome below afterwards.");
      onCalled();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start the call"); }
    setCalling(false);
  };
  return (
    <div className="space-y-2.5" data-testid="contact-call-panel">
      <div className="text-xs" style={{ color: "var(--sj-text-2)" }}>We'll ring your phone first, then connect you to {member.name} ({member.phone}). The call shows as Streams of Joy Erith London.</div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input data-testid="contact-callback-input" value={cb} onChange={(e) => setCb(e.target.value)} placeholder="Your phone, e.g. 07123 456789" className={inputCls} />
        <button type="button" data-testid="contact-call-now" disabled={calling} onClick={start} className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
          <PhoneCall className="h-4 w-4" /> {calling ? "Calling…" : "Call now"}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        <a data-testid="contact-call-dialer" href={`tel:${telDigits(member.phone)}`} onClick={onCalled} className={linkBtn} style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}><Phone className="h-3.5 w-3.5" /> Phone dialer</a>
        <a data-testid="contact-call-whatsapp" href={`https://wa.me/${waDigits(member.phone)}`} target="_blank" rel="noreferrer" onClick={onCalled} className={linkBtn} style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
      </div>
    </div>
  );
};

const TextPanel = ({ member, onSent }) => {
  const first = (member.name || "").split(" ")[0];
  const [msg, setMsg] = useState(`Hi ${first}, `);
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (msg.trim().length < 2) { toast.error("Write your message first"); return; }
    setSending(true);
    try { await api.post(`/members/${member.id}/sms`, { message: msg.trim() }); toast.success(`Text sent to ${first}`); onSent(msg.trim()); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send the text"); }
    setSending(false);
  };
  return (
    <div className="space-y-2.5" data-testid="contact-text-panel">
      <textarea data-testid="contact-sms-input" rows={3} maxLength={600} value={msg} onChange={(e) => setMsg(e.target.value)} className={inputCls + " resize-none"} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px]" style={{ color: "var(--sj-text-3)" }}>Sent from the church number to {member.phone}, signed with your first name. Saved to the log automatically.</span>
        <button type="button" data-testid="contact-sms-send" disabled={sending || msg.trim().length < 2} onClick={send} className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-60" style={{ background: "var(--sj-accent)", color: "#063B30" }}>
          <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send text & log"}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        <a data-testid="contact-text-sms-app" href={`sms:${telDigits(member.phone)}`} className={linkBtn} style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}><MessageSquare className="h-3.5 w-3.5" /> Use my phone's Messages</a>
        <a data-testid="contact-text-whatsapp" href={`https://wa.me/${waDigits(member.phone)}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" className={linkBtn} style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
      </div>
    </div>
  );
};

export const ContactActions = ({ member, kind, onCalled, onSent }) => {
  const { user } = useAuth();
  if (kind === "visit") return null;
  const canContact = user?.role === "admin" || user?.caps?.call_members;
  return (
    <div className="rounded-2xl p-4" style={{ background: "#F2F8F5", border: "1px solid #D5E6DD" }} data-testid="contact-actions">
      <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--sj-primary)" }}>{kind === "call" ? "Make the call" : "Send a text"}</div>
      {!member.phone ? <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>No phone number on record — you can still log the contact below.</p>
        : !canContact ? <p className="text-sm" data-testid="contact-not-permitted" style={{ color: "var(--sj-text-2)" }}>You're not permitted to {kind} members from the app. Ask an administrator to enable calling for your team — you can still log the contact below.</p>
        : kind === "call" ? <CallPanel member={member} onCalled={onCalled} /> : <TextPanel member={member} onSent={onSent} />}
    </div>
  );
};
